import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { addTargetApi, topicByID, updateTopic } from "./services/AppService";
import { StatusType } from "../../constants/Status";
import Toast from "../../component/ToastService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const LabelWithTooltip = ({ label, tooltip }) => (
  <div className="d-flex align-items-center mb-2">
    <Typography variant="body2" sx={{ marginRight: "4px", fontWeight: "bold" }}>
      {label}
    </Typography>
    <Tooltip title={tooltip} placement="top">
      <IconButton size="small" sx={{ padding: 0 }}>
        <InfoOutlinedIcon fontSize="small" color="action" />
      </IconButton>
    </Tooltip>
  </div>
);

const AddTarget = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const numericId = Number(id || 0);

  const [title, setTitle] = useState("Add Study Target");
  const [notes, setNotes] = useState("");
  const [formData, setFormData] = useState({
    topicName: "",
    date: "",
    targetDurationMinutes: "",
    title: "",
    description: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (numericId > 0) {
      setTitle("Edit Study Target");
      fetchTopic();
    }

    const mainElement = document.querySelector("main");
    if (mainElement) mainElement.style.height = "120vh";

    return () => {
      if (mainElement) mainElement.style.height = "";
    };
  }, [numericId]);

  const fetchTopic = async () => {
    try {
      const response = await topicByID(numericId);
      const data = await response.json();

      if (response.ok && data.status === "OK") {
        const d = data.data;
        setFormData({
          topicName: d.topicName,
          date: d.date || "",
          targetDurationMinutes: d.targetDurationMinutes || "",
          title: d.title || "",
          description: d.description || "",
        });
        setNotes(d.notes || "");
      } else {
        Toast(StatusType.ERROR, data.message || "Failed to load topic.");
      }
    } catch (error) {
      Toast(StatusType.ERROR, "Error fetching topic data");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.targetDurationMinutes) {
      newErrors.targetDurationMinutes = "Duration is required";
    } else if (isNaN(formData.targetDurationMinutes) || Number(formData.targetDurationMinutes) < 1) {
      newErrors.targetDurationMinutes = "Must be at least 1 minute";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
  
    const payload = {
      ...formData,
      notes,
    };
  
    try {
      const response = numericId > 0
        ? await updateTopic({ ...payload, id: numericId })
        : await addTargetApi(payload);
  
      const data = await response.json();
      console.log("data");
      console.log(data);
  
      if (["OK", "CREATED"].includes(data.status)) {
        Toast(StatusType.SUCCESS, data.message);
  
        setFormData({
          topicName: "",
          date: "",
          targetDurationMinutes: "",
          title: "",
          description: "",
        });
        setNotes("");
        navigate("/app/target");
      } else {
        Toast(StatusType.ERROR, data.message || "Something went wrong");
      }
    } catch (err) {
      Toast(StatusType.ERROR, "Submit failed");
    }
  };
  

  return (
    <Container maxWidth="lg">
      <Card variant="outlined" className="w-100 p-2">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            {title}
          </Typography>

          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              {/* Study Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<LabelWithTooltip label="Study Date" tooltip="Enter target study date." />}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  error={!!errors.date}
                  helperText={errors.date}
                />
              </Grid>

              {/* Duration */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<LabelWithTooltip label="Duration (min)" tooltip="Enter study duration in minutes" />}
                  type="number"
                  fullWidth
                  name="targetDurationMinutes"
                  value={formData.targetDurationMinutes}
                  onChange={handleChange}
                  error={!!errors.targetDurationMinutes}
                  helperText={errors.targetDurationMinutes}
                />
              </Grid>

              {/* Title */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<LabelWithTooltip label="Title" tooltip="Enter title for study topic." />}
                  fullWidth
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<LabelWithTooltip label="Description (optional)" tooltip="Enter description of study topic." />}
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    "& .ql-container": { height: "200px" },
                    "& .ql-editor": { height: "150px", overflowY: "auto" },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    placeholder="Enter your notes"
                    value={notes}
                    onChange={setNotes}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Buttons */}
            <Box mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={numericId > 0 ? null : <AddIcon />}
              >
                {numericId > 0 ? "Update" : "Submit"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                className="ms-2"
                startIcon={<CancelIcon />}
                onClick={() => navigate("/app/target")}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AddTarget;
